import { useTranslation } from 'react-i18next'
import { useEffect, useMemo, useState } from 'react'

import { FormProvider, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'

import { customersSchema } from '../../utils/schemas'

import HookedField from '../../components/HookedField'
import useClient from '../../hooks/useClient'
import { ICONS, PERMISSIONS } from '../../utils/constants'
import Icon from '../../components/Icon'
import { fetchLovChildrenByParentName } from '../../api/lov'

const CustomersForm = ({
  defaultValues = {},
  submitHandler,
  handleWalletAmountEdit,
  onSyncSap,
  sapSyncLoading,
  onSyncCoupons,
  couponsSyncLoading,
  onSyncWallet,
  walletSyncLoading,
}) => {
  const { t } = useTranslation()
  const { isClientCan } = useClient()
  const [genderOptions, setGenderOptions] = useState([])

  useEffect(() => {
    fetchLovChildrenByParentName('GENDER')
      .then((data) => {
        setGenderOptions(
          data.map((item) => ({
            label: item.descriptionEn || item.name,
            value: item.id,
          })),
        )
      })
      .catch((err) => console.error(err))
  }, [])

  const handleSubmit = (values) => {
    if (typeof submitHandler === 'function') {
      submitHandler(values, methods)
    }
  }

  const isClientCanUpdateWallet = isClientCan(PERMISSIONS.UPDATE_WALLET)
  const isClientCanUpdateUsers = isClientCan(PERMISSIONS.UPDATE_USERS)

  const methods = useForm({
    mode: 'onSubmit',
    shouldFocusError: true,
    resolver: yupResolver(customersSchema),
  })

  const {
    reset  
  } = methods
  
  useEffect(() => {
    if (!defaultValues?.users_id) return
  
    reset({
      id: defaultValues.users_id,
      sapId: defaultValues.users_sapId ?? '',
      nameEn: defaultValues.users_fullName ?? '',
      mobile: defaultValues.users_phoneNumber ?? '',
      secondaryPhone: defaultValues.users_secondaryPhone ?? '',
      birthday: defaultValues.users_birthday ?? '',
      genderId: defaultValues.users_genderId ?? null,
      email: defaultValues.users_email ?? '',
      totalOrders: defaultValues.totalorders ?? 0,
      lastOrder: defaultValues.lastorder ?? defaultValues.order_createdAt ?? '',
    })
  }, [defaultValues?.users_id, reset])

  return (
    <div className="customer-form">
      <FormProvider {...methods}>
        <form className="form" autoComplete="off" onSubmit={methods.handleSubmit(handleSubmit)}>
          <fieldset>
            <div className="form-title">{t('customers.customer info')}</div>
            <div className="form-grid" style={{ alignItems: 'center' }}>
              {defaultValues.users_id && (
                <HookedField
                  label={t('fields.id')}
                  name="id"
                  disabled={true}
                  defaultValue={defaultValues.users_id}
                  placeholder="id"
                  fieldType="textField"
                  type="text"
                />
              )}
              <HookedField
                label="Customer Code"
                name="sapId"
                disabled={true}
                defaultValue={defaultValues?.users_sapId || ''}
                placeholder="Customer Code"
                fieldType="textField"
                type="text"
                suffixIcon={
                  isClientCanUpdateUsers && (onSyncSap || onSyncCoupons) ? (
                    <div style={{ display: 'flex', gap: 8 }}>
                      {onSyncSap && (
                        <button
                          type="button"
                          className="customer-sap-sync-btn"
                          onClick={onSyncSap}
                          disabled={sapSyncLoading}
                          title={t('customers.syncCustomerCode', 'Sync customer code')}
                        >
                          <Icon icon={ICONS.PLAY} size={18} />
                        </button>
                      )}
                      {onSyncCoupons && (
                        <button
                          type="button"
                          className="customer-sap-sync-btn"
                          onClick={onSyncCoupons}
                          disabled={couponsSyncLoading}
                          title={t('customers.syncCoupons', 'Sync booklet')}
                        >
                          <Icon icon={ICONS.COUPON} size={18} />
                        </button>
                      )}
                    </div>
                  ) : null
                }
              />
              <HookedField
                label={t('fields.name en')}
                name="nameEn"
                defaultValue={defaultValues?.users_fullName || ''}
                placeholder={t('fields.name en')}
                fieldType="textField"
                type="text"
                disabled={true}
              />
              <HookedField
                label={t('fields.mobile')}
                name="mobile"
                defaultValue={defaultValues?.users_phoneNumber || ''}
                placeholder={t('fields.mobile')}
                fieldType="textField"
                type="tel"
                disabled={!isClientCanUpdateUsers}
              />
              <HookedField
                label={t('fields.secondary phone')}
                name="secondaryPhone"
                defaultValue={defaultValues?.users_secondaryPhone || ''}
                placeholder={t('fields.secondary phone')}
                fieldType="textField"
                type="tel"
                disabled={true}
              />
              <HookedField
                label={t('fields.birthday')}
                name="birthday"
                defaultValue={defaultValues?.users_birthday || ''}
                placeholder={t('fields.birthday')}
                fieldType="dateField"
                type="datePicker"
                disabled={true}
              />
              <HookedField
                label={t('fields.gender')}
                name="genderId"
                defaultValue={defaultValues?.users_genderId || null}
                placeholder={t('fields.gender')}
                fieldType="selectField"
                type="select"
                options={genderOptions}
                disabled={true}
                allowClear={true}
              />
              <HookedField
                label={t('fields.email')}
                name="email"
                defaultValue={defaultValues?.users_email || ''}
                placeholder={t('fields.email')}
                fieldType="textField"
                type="text"
                disabled={true}
              />
              <HookedField
                label={t('fields.total orders')}
                name="totalOrders"
                defaultValue={defaultValues?.totalorders || 0}
                placeholder={t('fields.total orders')}
                fieldType="numberField"
                type="number"
                disabled={true}
              />
              <HookedField
                label={t('fields.last order')}
                name="lastOrder"
                defaultValue={defaultValues?.lastorder || defaultValues?.order_createdAt || ''}
                placeholder={t('fields.last order')}
                fieldType="dateField"
                type="datePicker"
                disabled={true}
              />
              <HookedField
                label={t('fields.wallet')}
                name="lastOrder"
                defaultValue={''}
                placeholder={t('wallet.currency', {
                  amount: defaultValues?.walletBalance,
                })}
                fieldType="numberField"
                type="number"
                disabled={true}
                suffixIcon={
                  onSyncWallet && isClientCanUpdateWallet && (
                    <button
                      type="button"
                      className="customer-sap-sync-btn"
                      onClick={onSyncWallet}
                      disabled={walletSyncLoading}
                    >
                      <Icon icon={ICONS.PLAY} size={18} />
                    </button>
                  )
                }
              />
              {isClientCanUpdateUsers && (
                <div>
                  <button type="submit" className="btn btn--normal btn--primary">
                    {t('app.save')}
                  </button>
                </div>
              )}
              {/* {isClientCanUpdateWallet && (
                <div>
                  <button
                    type="button"
                    className="btn btn--normal btn--primary"
                    onClick={handleWalletAmountEdit}
                  >
                    Edit wallet
                  </button>
                </div>
              )} */}
            </div>
          </fieldset>
        </form>
      </FormProvider>
    </div>
  )
}

export default CustomersForm
